import * as React from 'react';
import { canEditBillable } from '@cfl/shared';

import {
  Alert, AlertDescription, Button, Checkbox, Combobox, Dialog, DialogContent,
  DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { fitsProject, useActivities, useProjects, useTags } from '@/lib/catalog-api';
import { useBulkUpdate } from '@/lib/reporting-api';
import { useSession } from '@/lib/session';

/**
 * Change project, activity, tags or billable across a selection.
 *
 * Only the fields somebody ticks are sent. An untouched field is left alone on
 * every entry rather than overwritten with a default - that distinction is the
 * whole reason this is a form of opt-in rows and not just a set of inputs.
 *
 * Deliberately does NOT offer moving entries to another person, or rates: the
 * first is a correction that deserves the deliberation of doing it one at a time,
 * and the second is money this system does not track.
 */
export function BulkEditDialog({
  ids,
  open,
  onClose,
  onDone,
}: {
  ids: string[];
  open: boolean;
  onClose: () => void;
  onDone: (updated: number) => void;
}) {
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const { data: tags } = useTags();
  const bulk = useBulkUpdate();
  const { data: session } = useSession();

  const [applyProject, setApplyProject] = React.useState(false);
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [applyActivity, setApplyActivity] = React.useState(false);
  const [activityId, setActivityId] = React.useState<string | null>(null);
  const [applyBillable, setApplyBillable] = React.useState(false);
  const [billable, setBillable] = React.useState(true);
  const [applyTags, setApplyTags] = React.useState(false);
  const [tagIds, setTagIds] = React.useState<string[]>([]);
  const [replaceTags, setReplaceTags] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setApplyProject(false); setProjectId(null);
    setApplyActivity(false); setActivityId(null);
    setApplyBillable(false); setBillable(true);
    setApplyTags(false); setTagIds([]); setReplaceTags(false);
    setError(null);
  }, [open]);

  // Only activities bookable on the chosen project, so the dialog cannot offer a
  // pairing the server will refuse.
  const activityOptions = (activities ?? []).filter(
    (a) => !applyProject || !projectId || fitsProject(a, projectId, projects),
  );

  const nothingChosen = !applyProject && !applyActivity && !applyBillable && !applyTags;

  const submit = async () => {
    setError(null);
    // A ticked field with nothing chosen used to be dropped silently, and the
    // dialog then reported entries "updated" that had not moved.
    if (applyProject && !projectId) return setError('Choose the project to move them to, or untick Project.');
    if (applyActivity && !activityId) return setError('Choose the activity to move them to, or untick Activity.');
    try {
      const { updated } = await bulk.mutateAsync({
        ids,
        ...(applyProject && projectId ? { projectId } : {}),
        ...(applyActivity && activityId ? { activityId } : {}),
        ...(applyBillable ? { billable } : {}),
        ...(applyTags ? { tagIds, replaceTags } : {}),
      });
      onDone(updated);
    } catch (err) {
      // The server names the entry and the reason - a locked week, an exported
      // entry, a project that had not started. That is the actionable part.
      setError(err instanceof ApiError ? err.message : 'Could not change those entries.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Change {ids.length} {ids.length === 1 ? 'entry' : 'entries'}
          </DialogTitle>
          <DialogDescription>
            Only the fields you tick are changed. Everything else is left as it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Row checked={applyProject} onCheckedChange={setApplyProject} label="Project">
            <Combobox
              value={projectId}
              onChange={(v) => { setProjectId(v); setActivityId(null); }}
              options={(projects ?? []).map((p) => ({
                value: p.id,
                label: `${p.name} · ${p.customer.name}`,
              }))}
              placeholder="Choose a project"
              searchPlaceholder="Search projects"
            />
          </Row>

          <Row checked={applyActivity} onCheckedChange={setApplyActivity} label="Activity">
            <Combobox
              value={activityId}
              onChange={setActivityId}
              options={activityOptions.map((a) => ({ value: a.id, label: a.name }))}
              placeholder="Choose an activity"
              searchPlaceholder="Search activities"
            />
          </Row>

          {/* Supervisors and up: for everyone else it follows the customer,
              project and activity, and the server refuses a choice. */}
          {canEditBillable(session?.role ?? 'EMPLOYEE') && (
            <Row checked={applyBillable} onCheckedChange={setApplyBillable} label="Billable">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={billable} onCheckedChange={(v) => setBillable(v === true)} />
                Mark as billable
              </label>
            </Row>
          )}

          <Row checked={applyTags} onCheckedChange={setApplyTags} label="Tags">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {(tags ?? []).map((tag) => {
                  const on = tagIds.includes(tag.id);
                  return (
                    <Button hint={"Add or remove this tag on every selected entry"}
                      key={tag.id}
                      type="button"
                      variant={on ? 'default' : 'outline'}
                      size="sm"
                      className="h-auto py-1 text-xs font-normal"
                      onClick={() =>
                        setTagIds((prev) =>
                          on ? prev.filter((id) => id !== tag.id) : [...prev, tag.id],
                        )
                      }
                    >
                      {tag.name}
                    </Button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={replaceTags}
                  onCheckedChange={(v) => setReplaceTags(v === true)}
                />
                Replace existing tags instead of adding to them
              </label>
            </div>
          </Row>
        </div>

        {error && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={bulk.isPending}>
            Cancel
          </Button>
          <Button hint={"Apply these changes to every selected entry"} onClick={() => void submit()} disabled={nothingChosen || bulk.isPending}>
            {bulk.isPending ? 'Changing…' : `Change ${ids.length}`}
          </Button>
          {nothingChosen && <span className="text-xs text-muted-foreground">Choose at least one change to apply</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A field that does nothing until it is ticked. */
function Row({
  checked,
  onCheckedChange,
  label,
  children,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5 rounded-md border p-3">
      <Label className="flex items-center gap-2 text-sm font-medium">
        <Checkbox checked={checked} onCheckedChange={(v) => onCheckedChange(v === true)} />
        {label}
      </Label>
      <div className={checked ? '' : 'pointer-events-none opacity-40'}>{children}</div>
    </div>
  );
}
